import { useRef, type KeyboardEvent, type MouseEvent } from 'react'
import { DocumentTextIcon } from '@heroicons/react/24/outline'

export type DriverNoteIconProps = {
  /** Line label, used for the accessible name. */
  context: string
  note?: string
  /** User-authored projection methodology. */
  methodology?: string
  /** `recipe.rationale`, shown as the methodology when the user has not written one. */
  recipeRationale?: string
  /** Read-only shape/recipe subline, e.g. "Flat 26.3%". */
  computedMethodology?: string | null
  canEdit?: boolean
  onClick?: (position: { x: number; y: number }) => void
}

function trimmed(value?: string | null): string {
  return value?.trim() ?? ''
}

/**
 * Driver-column annotation glyph. Always rendered so the column keeps a single rhythm: solid when
 * the line carries a note or methodology, faded when it does not. Click opens the line note popover.
 */
export default function DriverNoteIcon({
  context,
  note,
  methodology,
  recipeRationale,
  onClick,
}: DriverNoteIconProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const noteText = trimmed(note)
  const methodologyText = trimmed(methodology) || trimmed(recipeRationale)
  const isFilled = Boolean(noteText || methodologyText)
  const isInteractive = Boolean(onClick)
  const ariaLabel = isFilled ? `Notes — ${context}` : `Add note or methodology — ${context}`

  const openAt = (event: MouseEvent | KeyboardEvent) => {
    if (!onClick) return
    const rect = buttonRef.current?.getBoundingClientRect()
    if (rect && (rect.width || rect.height)) {
      onClick({ x: rect.left, y: rect.top })
      return
    }
    const mouse = event as MouseEvent
    onClick({ x: mouse.clientX ?? 0, y: mouse.clientY ?? 0 })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!onClick) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    openAt(event)
  }

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={ariaLabel}
      className={[
        'inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600',
        isInteractive ? 'cursor-pointer' : 'cursor-default',
        isFilled
          ? 'text-gray-500 opacity-100 hover:text-gray-700'
          : 'text-gray-400 opacity-40 hover:opacity-100',
      ].join(' ')}
      onMouseDown={(e) => e.preventDefault()}
      onClick={openAt}
      onKeyDown={handleKeyDown}
      disabled={!isInteractive}
    >
      <DocumentTextIcon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  )
}
